/**
 * Environment configuration.
 *
 * `VITE_`-prefixed values are baked into the build artifact and are therefore
 * PUBLIC (ARCHITECTURE §17). Only the Supabase URL and anon key belong here.
 * A service-role key, database password or provider API key must never carry a
 * VITE_ prefix and must never appear in this repository.
 */

export interface SupabaseEnv {
  url: string;
  anonKey: string;
}

function read(name: string): string {
  const env = import.meta.env as Record<string, unknown>;
  const value = env[name];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Returns the Supabase configuration, or null when it is absent.
 *
 * Null is a legitimate state today: no hosted Crewlink Supabase project exists
 * yet, and creating it is Jerry's action. The app must therefore start,
 * render, and say something truthful rather than crash on a blank screen —
 * which is what an unguarded client constructor would do.
 */
export function readSupabaseEnv(): SupabaseEnv | null {
  const url = read('VITE_SUPABASE_URL');
  const anonKey = read('VITE_SUPABASE_ANON_KEY');
  if (!url || !anonKey) return null;
  return { url, anonKey };
}

/** Sentry DSN, or null. Absent is normal — see `src/lib/monitoring.ts`. */
export function readSentryDsn(): string | null {
  return read('VITE_SENTRY_DSN') || null;
}

/**
 * The PUBLIC origin this Crewlink build's own web app is served from, or null.
 *
 * **Why this cannot be `window.location.origin` (H-3).** Password recovery is
 * the one flow where the app must name an address that a DIFFERENT program —
 * the person's email client, then their browser — will open later. On Android
 * the app runs inside a WebView whose origin is `https://localhost`, so
 * deriving the destination from the running page produced a reset link to
 * `https://localhost/reset-password`: valid-looking, and dead on arrival.
 *
 * So the destination is CONFIGURED, never inferred. It is a `VITE_` value and
 * therefore public and baked into the artifact (ARCHITECTURE §17), which is
 * correct — it is a published web address, not a credential.
 *
 * **Null means "not configured", and callers must treat that as a refusal
 * rather than a cue to guess.** Returning `window.location.origin` as a
 * fallback would reintroduce the exact bug this reader exists to remove, and
 * would do it silently, on the one flow nobody can test by using the app.
 *
 * **THE VALUE IS AN ORIGIN, AND ONLY AN ORIGIN.** It is normalized through
 * `URL.origin`, so a setting carrying a path, a query or a fragment is reduced
 * to the origin rather than concatenated into a malformed destination —
 * `https://host/app` + `/reset-password` would otherwise produce
 * `https://host/app/reset-password`, and a query or fragment would produce
 * something that is not a URL to the reset page at all. `URL.origin` also
 * removes a trailing slash on its own, so no separate trimming is needed.
 *
 * Validation is deliberately narrow: an absolute `http`/`https` address. A
 * relative path, a `capacitor://` scheme or a bare hostname cannot be resolved
 * by an email client, so they are refused rather than repaired.
 *
 * **A RELEASE BUILD REFUSES A LOOPBACK ORIGIN.** `http://localhost:5173` is a
 * legitimate answer while genuinely running a development server, and a
 * meaningless one in a shipped build — every recipient's own machine is a
 * different computer, so the emailed link resolves to whatever happens to be
 * running on theirs, or to nothing. Configuring it that way is a mistake that
 * would otherwise reach real inboxes, so a non-development build treats it as
 * absent. `import.meta.env.DEV` is the same signal the rest of this file and
 * `monitoring.ts` already use; nothing new was introduced to express it.
 */
export function readPublicAppUrl(): string | null {
  const raw = read('VITE_PUBLIC_APP_URL');
  if (!raw) return null;

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;

  if (!import.meta.env.DEV && isLoopbackHost(parsed.hostname)) return null;

  return parsed.origin;
}

/**
 * Hostnames that resolve to the machine doing the asking.
 *
 * `URL` normalizes an IPv6 literal by stripping its brackets, so `[::1]`
 * arrives here as `::1`. The list is deliberately short — this is a guard
 * against one configuration mistake, not a networking subsystem.
 */
function isLoopbackHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host === '::1' ||
    host === '[::1]' ||
    /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

/** Coarse environment label for monitoring. Never used for product behavior. */
export function readEnvironmentName(): string {
  return read('VITE_ENVIRONMENT') || (import.meta.env.DEV ? 'local' : 'unknown');
}

// `isRecorderProbeEnabled` (`VITE_ENABLE_RECORDER_PROBE`) lived here from
// M2 / S-02B-i until M3 / S-03-03, when the development recorder probe was
// retired with the flag that gated it. Recording is product surface now.
